import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Search, Download, Plus, X, Upload, FileText, AlertTriangle, Fuel, Trash2 } from "lucide-react";

const STATUSES = [
  { v: "", label: "Toutes" },
  { v: "en_attente", label: "En attente" },
  { v: "verifie_ecole", label: "Vérifié école" },
  { v: "valide", label: "Validé" },
  { v: "envoye", label: "Envoyé" },
  { v: "refuse", label: "Refusé" },
];

const STATUS_CLASS = {
  en_attente: "bg-amber-50 text-amber-700 border-amber-200",
  verifie_ecole: "bg-sky-50 text-sky-700 border-sky-200",
  valide: "bg-blue-50 text-blue-700 border-blue-200",
  envoye: "bg-emerald-50 text-emerald-700 border-emerald-200",
  refuse: "bg-red-50 text-red-700 border-red-200",
};

const fmtDate = (s) => (s ? new Date(s).toLocaleDateString("fr-FR") : "—");

export default function AdminBons() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [statut, setStatut] = useState("");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = {};
      if (statut) params.statut = statut;
      if (q.trim()) params.q = q.trim();
      const { data } = await api.get("/admin/bons", { params });
      setItems(data.items || []);
      setCounts(data.counts || {});
      setSelectedIds(new Set());
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  }, [statut, q]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allSelected = items.length > 0 && items.every((d) => selectedIds.has(d.id));
  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(items.map((d) => d.id)));
  };

  const deleteSelection = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer définitivement ${ids.length} demande(s) sélectionnée(s) ? Cette action est irréversible.`)) return;
    setDeleting(true);
    try {
      const { data } = await api.post("/admin/bons/bulk-delete", { ids });
      toast.success(`${data.deleted || ids.length} demande(s) supprimée(s).`);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setDeleting(false);
    }
  };

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/admin/bons/export", {
        params: statut ? { statut } : {},
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "bons_carburant.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="flex items-center gap-2 text-3xl font-extrabold tracking-tighter">
              <Fuel className="h-7 w-7 text-brand" /> Bons carburant
            </h1>
            <p className="mt-2 text-neutral-500">
              25 € par élève ayant réussi son permis via Plan Permis — une seule fois par personne.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowCreate(true)}
              data-testid="bon-create-btn"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-hover"
            >
              <Plus className="h-4 w-4" /> Nouvelle demande
            </button>
            <button
              onClick={deleteSelection}
              disabled={selectedIds.size === 0 || deleting}
              data-testid="bon-bulk-delete-btn"
              className="inline-flex items-center gap-2 rounded-full border border-red-300 px-4 py-2.5 text-sm font-bold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Trash2 className="h-4 w-4" /> Supprimer la sélection{selectedIds.size ? ` (${selectedIds.size})` : ""}
            </button>
            <button
              onClick={exportCsv}
              data-testid="bon-export-btn"
              className="inline-flex items-center gap-2 rounded-full border border-neutral-300 px-4 py-2.5 text-sm font-bold text-neutral-700 hover:border-brand hover:text-brand"
            >
              <Download className="h-4 w-4" /> Export CSV
            </button>
          </div>
        </div>

        {/* Filtres statut */}
        <div className="mt-6 flex flex-wrap gap-2" data-testid="bon-status-filters">
          {STATUSES.map((s) => (
            <button
              key={s.v}
              onClick={() => setStatut(s.v)}
              data-testid={`bon-filter-${s.v || "all"}`}
              className={`rounded-full px-3.5 py-1.5 text-sm font-semibold transition-colors ${
                statut === s.v ? "bg-brand text-white" : "bg-white text-neutral-600 border border-neutral-200 hover:border-brand"
              }`}
            >
              {s.label}
              {s.v && counts[s.v] != null ? ` (${counts[s.v]})` : ""}
            </button>
          ))}
        </div>

        {/* Recherche */}
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-neutral-200 bg-white px-3 py-2 sm:max-w-sm">
          <Search className="h-4 w-4 text-neutral-400" />
          <input
            data-testid="bon-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (email, téléphone, nom)…"
            className="w-full text-sm outline-none"
          />
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto rounded-lg border border-neutral-200 bg-white">
          <table className="w-full text-sm" data-testid="admin-bons-table">
            <thead className="bg-neutral-50 text-left text-[11px] uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={toggleAll}
                    data-testid="bon-select-all"
                    className="h-4 w-4 cursor-pointer accent-brand"
                    aria-label="Tout sélectionner"
                  />
                </th>
                <th className="px-4 py-3">Demande</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Auto-école</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3">Bon reçu</th>
                <th className="px-4 py-3">Créée</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-100">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-neutral-400">
                    Aucune demande pour ce filtre.
                  </td>
                </tr>
              ) : (
                items.map((d) => (
                  <tr
                    key={d.id}
                    onClick={() => setSelected(d)}
                    data-testid={`bon-row-${d.id}`}
                    className={`cursor-pointer hover:bg-neutral-50 ${selectedIds.has(d.id) ? "bg-brand/5" : ""}`}
                  >
                    <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(d.id)}
                        onChange={() => toggleOne(d.id)}
                        data-testid={`bon-select-${d.id}`}
                        className="h-4 w-4 cursor-pointer accent-brand"
                        aria-label={`Sélectionner ${d.first_name} ${d.last_name}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-semibold text-neutral-800">
                        {d.first_name} {d.last_name}
                        {d.alerte_admin && (
                          <span title={d.alerte_motif} data-testid={`bon-alert-${d.id}`}>
                            <AlertTriangle className="h-4 w-4 text-amber-500" />
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-neutral-400">{d.date_naissance || "—"}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-neutral-700">{d.email}</div>
                      <div className="text-xs text-neutral-400">{d.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{d.auto_ecole || "—"}</td>
                    <td className="px-4 py-3">
                      <span className={`rounded-full border px-2.5 py-1 text-xs font-semibold ${STATUS_CLASS[d.statut] || ""}`}>
                        {d.statut_label}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {d.bon_recu ? (
                        <span className="text-xs font-semibold text-emerald-700">Oui · {fmtDate(d.date_reception)}</span>
                      ) : (
                        <span className="text-xs text-neutral-400">Non</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-neutral-500">{fmtDate(d.date_demande)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {selected && <DetailModal demande={selected} onClose={() => setSelected(null)} onUpdated={load} />}
      {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={load} />}
    </Shell>
  );
}

function DetailModal({ demande, onClose, onUpdated }) {
  const [d, setD] = useState(demande);
  const [statut, setStatut] = useState(demande.statut);
  const [motif, setMotif] = useState(demande.motif_refus || "");
  const [notes, setNotes] = useState(demande.notes || "");
  const [saving, setSaving] = useState(false);
  const [file, setFile] = useState(null);

  const preuveUrl = `${API}/admin/bons/${d.id}/preuve`;

  const save = async () => {
    setSaving(true);
    try {
      const payload = { statut, notes };
      if (statut === "refuse") payload.motif_refus = motif;
      const { data } = await api.patch(`/admin/bons/${d.id}/statut`, payload);
      setD(data);
      toast.success("Demande mise à jour.");
      onUpdated();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const uploadPreuve = async () => {
    if (!file) return;
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("preuve", file);
      const { data } = await api.post(`/admin/bons/${d.id}/preuve`, fd);
      setD(data);
      setFile(null);
      toast.success("Preuve ajoutée.");
      onUpdated();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="bon-detail-modal"
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-extrabold text-neutral-900">
              {d.first_name} {d.last_name}
            </h2>
            <p className="text-sm text-neutral-500">{d.email} · {d.phone}</p>
          </div>
          <button onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100" data-testid="bon-modal-close">
            <X className="h-5 w-5" />
          </button>
        </div>

        {d.alerte_admin && (
          <div className="mt-4 flex items-start gap-2 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{d.alerte_motif}</span>
          </div>
        )}

        <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
          <div><dt className="text-neutral-400">Date naissance</dt><dd className="font-medium">{d.date_naissance || "—"}</dd></div>
          <div><dt className="text-neutral-400">Auto-école</dt><dd className="font-medium">{d.auto_ecole || "—"}</dd></div>
          <div><dt className="text-neutral-400">Forfait choisi</dt><dd className="font-medium">{d.forfait_choisi || "—"}</dd></div>
          <div><dt className="text-neutral-400">Date réussite</dt><dd className="font-medium">{d.date_reussite || "—"}</dd></div>
          <div><dt className="text-neutral-400">Bon reçu</dt><dd className="font-medium">{d.bon_recu ? `Oui (${fmtDate(d.date_reception)})` : "Non"}</dd></div>
        </dl>

        {/* Preuve */}
        <div className="mt-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Preuve</span>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {d.has_preuve && (
              <a
                href={preuveUrl}
                target="_blank"
                rel="noreferrer"
                data-testid="bon-view-preuve"
                className="inline-flex items-center gap-1.5 rounded-lg border border-neutral-300 px-3 py-1.5 text-sm font-semibold text-neutral-700 hover:border-brand hover:text-brand"
              >
                <FileText className="h-4 w-4" /> Voir la preuve
              </a>
            )}
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-dashed border-neutral-300 px-3 py-1.5 text-sm text-neutral-600 hover:border-brand">
              <Upload className="h-4 w-4" />
              {file ? file.name : (d.has_preuve ? "Remplacer" : "Ajouter")}
              <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="bon-upload-preuve" />
            </label>
            {file && (
              <button onClick={uploadPreuve} disabled={saving} className="rounded-lg bg-brand px-3 py-1.5 text-sm font-bold text-white disabled:opacity-60">
                Téléverser
              </button>
            )}
          </div>
        </div>

        {/* Statut */}
        <div className="mt-5">
          <label className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Statut</label>
          <select
            value={statut}
            onChange={(e) => setStatut(e.target.value)}
            data-testid="bon-status-select"
            className="mt-1 w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand"
          >
            <option value="en_attente">En attente</option>
            <option value="verifie_ecole">Vérifié par l’école</option>
            <option value="valide">Validé</option>
            <option value="envoye">Envoyé (bon remis)</option>
            <option value="refuse">Refusé</option>
          </select>
          {statut === "refuse" && (
            <textarea
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
              rows={2}
              placeholder="Motif du refus (envoyé à l'élève par email)"
              data-testid="bon-refus-motif"
              className="mt-2 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand"
            />
          )}
          {statut === "envoye" && (
            <p className="mt-2 text-xs text-emerald-700">
              Passer à « Envoyé » marque le bon comme reçu et envoie l’email de confirmation à l’élève.
            </p>
          )}
        </div>

        <div className="mt-4">
          <label className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Notes internes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            data-testid="bon-notes"
            className="mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-brand"
          />
        </div>

        {/* Historique */}
        {Array.isArray(d.history) && d.history.length > 0 && (
          <div className="mt-4 rounded-lg bg-neutral-50 p-3">
            <span className="text-xs font-semibold uppercase tracking-wide text-neutral-400">Historique</span>
            <ul className="mt-2 space-y-1 text-xs text-neutral-600">
              {d.history.map((h, i) => (
                <li key={i}>
                  <span className="text-neutral-400">{fmtDate(h.at)} — </span>
                  {h.event}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold text-neutral-700 hover:border-neutral-400">
            Fermer
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="bon-save-btn"
            className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-60"
          >
            {saving ? "Enregistrement…" : "Enregistrer"}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    date_naissance: "",
    auto_ecole: "",
    date_reussite: "",
    forfait_choisi: "",
    notes: "",
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full rounded-lg border border-neutral-300 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand";

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name || !form.last_name || !form.email || !form.phone) {
      toast.error("Nom, prénom, email et téléphone sont requis.");
      return;
    }
    setSaving(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      if (file) fd.append("preuve", file);
      await api.post("/admin/bons", fd);
      toast.success("Demande créée.");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <form
        onSubmit={submit}
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="bon-create-modal"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-extrabold text-neutral-900">Nouvelle demande de bon</h2>
          <button type="button" onClick={onClose} className="rounded-md p-1 text-neutral-400 hover:bg-neutral-100">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <input className={inputCls} placeholder="Prénom" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="bon-create-firstname" />
          <input className={inputCls} placeholder="Nom" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="bon-create-lastname" />
        </div>
        <input type="email" className={`${inputCls} mt-3`} placeholder="Email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="bon-create-email" />
        <input className={`${inputCls} mt-3`} placeholder="Téléphone" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="bon-create-phone" />
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label><span className="mb-1 block text-xs text-neutral-500">Date naissance</span><input type="date" className={inputCls} value={form.date_naissance} onChange={(e) => set("date_naissance", e.target.value)} /></label>
          <label><span className="mb-1 block text-xs text-neutral-500">Date réussite</span><input type="date" className={inputCls} value={form.date_reussite} onChange={(e) => set("date_reussite", e.target.value)} /></label>
        </div>
        <input className={`${inputCls} mt-3`} placeholder="Auto-école" value={form.auto_ecole} onChange={(e) => set("auto_ecole", e.target.value)} />
        <input className={`${inputCls} mt-3`} placeholder="Forfait choisi auprès de l'auto-école" value={form.forfait_choisi} onChange={(e) => set("forfait_choisi", e.target.value)} data-testid="bon-create-forfait" />
        <textarea className={`${inputCls} mt-3`} rows={2} placeholder="Notes internes (optionnel)" value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        <label className="mt-3 flex cursor-pointer items-center gap-2 rounded-lg border border-dashed border-neutral-300 px-3 py-2.5 text-sm text-neutral-600 hover:border-brand">
          <Upload className="h-4 w-4" />
          {file ? file.name : "Joindre une preuve (optionnel)"}
          <input type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </label>
        <div className="mt-6 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full border border-neutral-300 px-5 py-2.5 text-sm font-bold text-neutral-700">Annuler</button>
          <button type="submit" disabled={saving} data-testid="bon-create-submit" className="rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-60">
            {saving ? "Création…" : "Créer la demande"}
          </button>
        </div>
      </form>
    </div>
  );
}
